import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { authorisePayment, inr } from "@/lib/subscription";
import { PlanGrid, CycleToggle, usePlans } from "@/components/PlanCards";
import { Button } from "@/components/ui/button";
import { Loader } from "@/components/EmptyState";
import { AlertTriangle, ShieldCheck, RotateCcw } from "lucide-react";

export function PlanPicker() {
  const data = usePlans();
  const { business, refresh } = useAuth();
  const navigate = useNavigate();
  const [cycle, setCycle] = useState("yearly");
  const [busyId, setBusyId] = useState(null);
  const [failed, setFailed] = useState(null);

  if (!data) return <Loader />;

  const pick = async (plan, seats) => {
    setBusyId(plan.id); setFailed(null);
    try {
      const { data: start } = await api.post("/subscription/start", { plan_id: plan.id, cycle, seats });
      await authorisePayment(start, business);
      toast.success("Your 14-day free trial has started");
      await refresh();
      navigate("/app", { replace: true });
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Payment failed";
      api.post("/subscription/payment-failed").catch(() => {});
      setFailed({ msg, plan, seats });
    } finally { setBusyId(null); }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6" data-testid="plan-picker">
      <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#C85A32]">Last step</p>
      <h1 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-stone-900 sm:text-4xl">Choose your plan</h1>
      <p className="mt-3 max-w-2xl text-stone-600">
        Every plan starts with a <b>{data.trial_days}-day free trial</b>. You'll authorise payment now, but <b>nothing is charged today</b> — the first charge happens when the trial ends, and only for the plan you confirm here.
      </p>
      <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
        <CycleToggle cycle={cycle} setCycle={setCycle} />
        <p className="flex items-center gap-2 text-sm text-stone-500"><ShieldCheck className="h-4 w-4 text-[#0D5C3A]" /> Prices in ₹ per employee. Totals use each plan's employee capacity.</p>
      </div>
      {data.gateway === "mock" && <p className="mt-4 rounded-xl bg-amber-50 px-4 py-2 text-sm text-amber-800" data-testid="plan-mock-banner">Test mode: payments aren't connected yet, so Start Free Trial simulates a successful payment.</p>}
      {failed && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4" data-testid="plan-payment-error">
          <p className="flex items-center gap-2 text-sm text-rose-700"><AlertTriangle className="h-4 w-4" /> {failed.msg}. Your dashboard unlocks once payment is authorised.</p>
          <Button onClick={() => pick(failed.plan, failed.seats)} className="bg-rose-600 hover:bg-rose-700" data-testid="plan-retry-btn"><RotateCcw className="mr-1 h-4 w-4" /> Retry Payment ({failed.plan.name}, {inr(failed.plan.monthly)}/employee)</Button>
        </div>
      )}
      <div className="mt-8"><PlanGrid plans={data.plans} cycle={cycle} busyId={busyId} onPick={pick} /></div>
    </div>
  );
}
